import MUIDataTable from 'mui-datatables';
import { Paper, Stack, Button, Typography, Box, Grid, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { searchFulfilled, selectTotal, selectUsers } from '../../Search/searchSlice';
import { variants } from '../../../variants/variants';
import { useEffect, useMemo, useState } from 'react';
import { useLazySearchEmployeeQuery } from '../../Search/searchApiSlice';
import UserAvatar from '../../../common/UserAvatar';
import CustomRow from './CustomRow';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { selectUserProfile, setSelectedFeatures, setSelectedUsers, setSidebar, setUserProfile } from '../userSlice';
import { useGetAllEmployeesQuery } from '../../RnR/rnrApiSlice';

const UserTable = () => {
  const { pathname } = useLocation();
  const { empCode } = useParams();
  const permissionOpen = pathname.split('/').includes('permissions');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState({
    page: 1,
    searchTerm: '',
    selectedFilter: '',
  });
  // const total = useSelector(selectTotal);
  // const users = useSelector(selectUsers);
  // const [fetchUsers] = useLazySearchEmployeeQuery();
  const { data: users } = useGetAllEmployeesQuery();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const open = Boolean(empCode) || permissionOpen || isMobile;

  const genderOptions = [...new Set(users?.map(d => d.gender).filter(v => v && v.trim() !== ''))];
  const designationOptions = [...new Set(users?.map(d => d.designation).filter(v => v && v.trim() !== ''))];
  const departmentOptions = [...new Set(users?.map(d => d.department).filter(v => v && v.trim() !== ''))];
  const regionOptions = [...new Set(users?.map(d => d.region).filter(v => v && v.trim() !== ''))];


  const handleRowClick = (data) => {
    dispatch(setSidebar({ value: true }));
    dispatch(setUserProfile(data));
    dispatch(setSelectedFeatures({ signal: data[3] }));
    navigate(`/admin/user/${data[3]}`);
  };

  //function to call fetch query hook and set the values even when data is cached
  // const getCachedUsers = async () => {
  //   try {
  //     const data = await fetchUsers(search, { preferCacheValue: true }).unwrap();
  //     dispatch(searchFulfilled(data));
  //   } catch (err) {
  //     console.log(err);
  //   }
  // };

  //when mail drawer is closed : use default row
  const baseOptions = useMemo(() => {
    return {
      elevation: 0,
      rowsPerPage: 10,
      selectableRows: 'multiple',
      searchAlwaysOpen: true,
      selectToolbarPlacement: open ? 'none' : 'replace',
      selectableRowsHideCheckboxes: false,
      rowsPerPageOptions: [10, 20, 30],
      download: false,
      sort: false,
      print: false,
      filter: true,
      search: true,
      viewColumns: false,
      tableBodyHeight: 'calc(100vh - 270px)',
      responsive: 'standard',
      // onChangePage: currentPage => {
      //   fetchUsers(search);
      // },
      onRowsSelect: (currentRowsSelected, allRowsSelected) => {
        const selectedUsers = allRowsSelected.map((row) => users[row.dataIndex]);
        dispatch(setSelectedUsers(selectedUsers));
      },
      onRowClick: handleRowClick,
      setRowProps: () => {
        return {
          style: {
            cursor: 'default',
          },
        };
      },
    };
  }, [users, open, search]);

  const columns = [
    { name: '_id', options: { display: false, filter: false } },
    {
      name: 'userName',
      label: 'Employee',
      options: {
        filter: false,
        sort: false,
        search: true,
        customBodyRender: (value, tableMeta) => {
          const row = tableMeta.rowData;
          const email = row[2];
          const employeeCode = row[3];
          const gender = row[4];
          const photo = row[5];
          return open ? (
            <CustomRow user={row} />
          ) : (
            <Stack direction={'row'} alignItems={'center'}>
              <UserAvatar src={photo} />
              <Box sx={{ ml: 2 }}>
                <Typography sx={{ fontSize: variants.BODY }}>{value}</Typography>
                {/* <Typography sx={{ fontSize: variants.BODY }}>{email}</Typography> */}
                <Typography sx={{ fontSize: variants.TINY }}>{gender}</Typography>
              </Box>
            </Stack>
          );
        },
      },
    },
    {
      name: 'userName',
      label: 'Name',
      options: {
        display: false,
        filter: false,
        search: true,
      },
    },
    {
      name: 'empCode',
      label: 'Employee code',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'gender',
      label: 'Gender',
      options: {
        display: false,
        filter: true,
        filterOptions: {
          fullWidth: true,
          names: genderOptions,
        },
      },
    },
    {
      name: 'employeePhotoPath',
      label: 'Photo',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'designation',
      label: 'Designation',
      options: {
        filter: true,
        sort: false,
        display: !open,
        filterOptions: {
          fullWidth: true,
          names: designationOptions,
        },
      },
    },
    {
      name: 'department',
      label: 'Department',
      options: {
        filter: true,
        sort: false,
        display: !open,
        filterOptions: {
          fullWidth: true,
          names: departmentOptions,
        },
      },
    },

    {
      name: 'region',
      label: 'Region',
      options: {
        sort: false,
        display: !open,
        filterOptions: {
          fullWidth: true,
          names: regionOptions,
        },
      },
    },

    {
      name: 'experience',
      label: 'Experience',
      options: {
        filter: false,
        sort: false,
        display: !open,

        customBodyRender: (val) => {
          return Math.floor(val) || 0;
        },
      },
    },
    {
      name: 'techStack',
      label: 'Tech stack',
      options: {
        filter: false,
        sort: false,
        display: !open,
      },
    },
  ];
  // useEffect(() => {
  //   getCachedUsers();
  // }, []);
  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        mt: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: variants.boxRadius.MAIN,
        overflow: 'hidden',
        '& .MuiTableCell-root': {
          paddingLeft: 0,
        },
      }}
    >
      <MUIDataTable title={''} data={users} columns={columns} options={baseOptions} />
    </Box>
  );
};

export default UserTable;
